import type { LinkedJobContext, LinkedJobSummary } from '@/types/timeline'

export interface LinkableJob extends LinkedJobSummary {
  bookingId?: string
}

export const hasSharedBookingId = (firstBookingId?: string, secondBookingId?: string): boolean => {
  const first = firstBookingId?.trim()
  const second = secondBookingId?.trim()
  return Boolean(first && first === second)
}

const compareLinkedJobs = (a: LinkedJobSummary, b: LinkedJobSummary): number =>
  a.startTime.localeCompare(b.startTime) ||
  (a.endTime ?? '').localeCompare(b.endTime ?? '') ||
  a.type.localeCompare(b.type) ||
  a.id.localeCompare(b.id)

export const buildLinkedJobContexts = (
  jobs: readonly LinkableJob[]
): Map<string, LinkedJobContext> => {
  const jobsByBookingId = new Map<string, Map<string, LinkedJobSummary>>()

  for (const job of jobs) {
    const bookingId = job.bookingId?.trim()
    if (!bookingId) continue

    const bookingJobs = jobsByBookingId.get(bookingId) ?? new Map<string, LinkedJobSummary>()
    bookingJobs.set(job.id, {
      id: job.id,
      type: job.type,
      startTime: job.startTime,
      endTime: job.endTime,
      fromCode: job.fromCode,
      toCode: job.toCode,
    })
    jobsByBookingId.set(bookingId, bookingJobs)
  }

  const contexts = new Map<string, LinkedJobContext>()

  for (const bookingJobs of jobsByBookingId.values()) {
    if (bookingJobs.size < 2) continue

    const sortedJobs = [...bookingJobs.values()].toSorted(compareLinkedJobs)
    for (const job of sortedJobs) {
      contexts.set(job.id, {
        totalJobs: sortedJobs.length,
        relatedJobs: sortedJobs.filter((relatedJob) => relatedJob.id !== job.id),
      })
    }
  }

  return contexts
}
